import classNames from "classnames";

import type {
  InvocationBreakdownGroup,
  InvocationGroupBy,
} from "@/shared/domain/actions";
import { Toggle, ToggleGroup } from "@/shared/foundation/primitives";

import styles from "./InvocationActivity.module.css";

const GROUP_BY_OPTIONS: ReadonlyArray<{
  value: InvocationGroupBy;
  label: string;
}> = [
  { value: "action", label: "Action" },
  { value: "source", label: "Source" },
  { value: "submitter", label: "Submitter" },
];

export interface WaitingBreakdownProps {
  /** Largest first, and never empty: the caller drops the section when nothing is waiting. */
  groups: InvocationBreakdownGroup[];
  groupBy: InvocationGroupBy[];
  /** Never called with an empty selection; a breakdown by nothing has no rows to show. */
  onGroupByChange: (groupBy: InvocationGroupBy[]) => void;
}

/** What is waiting right now, cut by whichever dimensions the reader picks. */
export function WaitingBreakdown({
  groups,
  groupBy,
  onGroupByChange,
}: WaitingBreakdownProps) {
  const columns = GROUP_BY_OPTIONS.filter((option) =>
    groupBy.includes(option.value),
  );

  return (
    <section className={classNames(styles.section, styles.sectionDivided)}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Waiting now, by</h2>
        <ToggleGroup<InvocationGroupBy>
          aria-label="Group waiting invocations by"
          size="sm"
          multiple
          value={groupBy}
          onValueChange={(groupValue) => {
            if (groupValue.length > 0) {
              onGroupByChange(groupValue);
            }
          }}
        >
          {GROUP_BY_OPTIONS.map((option) => (
            <Toggle
              key={option.value}
              value={option.value}
              className={styles.blueToggle}
            >
              {option.label}
            </Toggle>
          ))}
        </ToggleGroup>
      </div>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.value}>{column.label}</th>
            ))}
            <th className={styles.numeric}>Waiting</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <tr key={groupKey(group)}>
              {columns.map((column) => (
                <td key={column.value}>{cellFor(group, column.value)}</td>
              ))}
              <td className={styles.numeric}>{group.count.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/**
 * What makes a group itself, so a row keeps its DOM when a refresh reorders the list. `trigger_name`
 * is left out: it is a lookup of `source_id`, so it can change without the group changing.
 */
function groupKey(group: InvocationBreakdownGroup): string {
  return JSON.stringify([
    group.action_org_id,
    group.action_name,
    group.source_type,
    group.source_id,
  ]);
}

function cellFor(
  group: InvocationBreakdownGroup,
  dimension: InvocationGroupBy,
) {
  switch (dimension) {
    case "action":
      return group.action_name ?? "";
    case "source":
      return group.source_type === "Manual"
        ? "User"
        : (group.source_type ?? "");
    case "submitter":
      return group.trigger_name !== null
        ? `Trigger: ${group.trigger_name}`
        : (group.source_id ?? "");
  }
}
